import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { toast } from '../../client/toast.js';
import { escapeHtml } from '../../shared/utils.js';
import componentStyles from './admin-notifications.css?inline';

export class AdminNotifications extends BaseComponent {
  private users: Array<{ id: string; name: string; email: string }> = [];

  protected styles(): string {
    return componentStyles;
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.loadUsers();
  }

  private async loadUsers(): Promise<void> {
    try {
      const response = await api.getAdminUsers();
      if (response.success && response.data) {
        this.users = response.data;
        this.render();
        this.afterRender();
      } else {
        toast.error(response.error || 'Failed to load users');
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    }
  }

  private async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);

    const userId = formData.get('userId') as string;
    const type = formData.get('type') as string;
    const title = formData.get('title') as string;
    const message = formData.get('message') as string;
    const actionUrl = formData.get('actionUrl') as string;

    if (!userId || !title || !message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (userId === 'all') {
        let successCount = 0;
        let errorCount = 0;

        for (const user of this.users) {
          const response = await api.createNotification({
            userId: user.id,
            type: type as 'welcome' | 'milestone' | 'admin' | 'analytics',
            title,
            message,
            actionUrl: actionUrl || undefined,
          });

          if (response.success) {
            successCount++;
          } else {
            errorCount++;
          }
        }

        if (errorCount === 0) {
          toast.success(`Notification sent to all ${successCount} users!`);
        } else {
          toast.error(`Sent to ${successCount} users, failed for ${errorCount} users`);
        }
        form.reset();
      } else {
        // Send to single user
        const response = await api.createNotification({
          userId,
          type: type as 'welcome' | 'milestone' | 'admin' | 'analytics',
          title,
          message,
          actionUrl: actionUrl || undefined,
        });

        if (response.success) {
          toast.success('Notification sent successfully!');
          form.reset();
        } else {
          toast.error(response.error || 'Failed to send notification');
        }
      }
    } catch (error) {
      console.error('Failed to create notification:', error);
      toast.error('Failed to send notification');
    }
  }

  protected template(): string {
    return `
      <ag-panel>
        <ag-panel-header>
          <h2>Send Notification</h2>
          <p class="description">Create and send notifications to users. Admin only.</p>
        </ag-panel-header>
        <ag-panel-content>
          <form id="notificationForm" class="notification-form">
            <ag-form-group>
              <label for="userId">Send to User *</label>
              <select id="userId" name="userId" required>
                <option value="">Select a user...</option>
                <option value="all">All Users</option>
                ${this.users.map(user => `
                  <option value="${user.id}">${escapeHtml(user.name)} (${escapeHtml(user.email)})</option>
                `).join('')}
              </select>
            </ag-form-group>

            <ag-form-group>
              <label for="type">Type</label>
              <select id="type" name="type">
                <option value="admin">Admin</option>
                <option value="welcome">Welcome</option>
                <option value="milestone">Milestone</option>
                <option value="analytics">Analytics</option>
              </select>
            </ag-form-group>

            <ag-form-group>
              <label for="title">Title *</label>
              <input type="text" id="title" name="title" placeholder="Notification title" required maxlength="100">
            </ag-form-group>

            <ag-form-group>
              <label for="message">Message *</label>
              <textarea id="message" name="message" placeholder="Notification message" required maxlength="500" rows="4"></textarea>
            </ag-form-group>

            <ag-form-group>
              <label for="actionUrl">Action URL (optional)</label>
              <input type="url" id="actionUrl" name="actionUrl" placeholder="https://example.com/action">
              <small class="text-muted">Optional link for users to click on in the notification</small>
            </ag-form-group>

            <div class="ag-button-group right">
              <button type="submit" class="btn btn-secondary">Send Notification</button>
            </div>
        </form>
      </div>
    `;
  }

  protected afterRender(): void {
    const form = this.$('#notificationForm');
    form?.addEventListener('submit', (e) => this.handleSubmit(e));
  }
}

customElements.define('admin-notifications', AdminNotifications);
